import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as invoiceService from '../services/invoiceService';
import {
  Box, Button, Card, CardContent, Typography, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Divider, Chip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from "react-i18next";

const InvoiceViewPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        const data = await invoiceService.getInvoiceById(id);
        setInvoice(data);
      } catch (error) {
        setInvoice(null);
      } finally {
        setLoading(false);
      }
    };
    fetchInvoice();
  }, [id]);

  if (loading) return <div>{t("loading")}</div>;
  if (!invoice) return <div>{t("invoice_not_found")}</div>;

  // Calculate totals from line items
  const totalExcl = invoice.LineItems?.reduce((sum, p) => sum + Number(p.TotalExcl || p.totalExcl || 0), 0) || 0;
  const totalVAT = invoice.LineItems?.reduce((sum, p) => sum + ((Number(p.TotalExcl || p.totalExcl || 0) * Number(p.VAT || p.vat || 0)) / 100), 0) || 0;
  const totalIncl = totalExcl + totalVAT;

  // Add handlers for edit and delete (implement as needed)
  const handleEdit = () => {
    navigate(`/invoices/edit/${invoice.Id}`);
  };
  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this invoice?')) {
      await invoiceService.deleteInvoice(invoice.Id);
      navigate('/invoices');
    }
  };

  return (
    <Box sx={{ maxWidth: 900, margin: '32px auto', background: '#fff', borderRadius: 2, boxShadow: 2, p: 4 }}>
      <Button variant="outlined" onClick={() => navigate('/invoices')} sx={{ mb: 2 }}>
        {t("back_to_invoices")}
      </Button>
      <Divider sx={{ mb: 3 }} />

      {/* Invoice Info full width */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h5" color="primary" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
              {t("invoice_info")}
            </Typography>
            <Typography><strong>{t("invoice_id")}:</strong> {invoice.Id}</Typography>
            <Typography><strong>{t("invoice_name")}:</strong> {invoice.InvoiceName}</Typography>
            <Typography>
              <strong>{t("status")}:</strong>{' '}
              <Chip
                label={
                  invoice.InvoiceDueAmount === 0 ? t("paid") :
                  invoice.InvoicePaidAmount > 0 ? t("partially_paid") :
                  t("unpaid")
                }
                color={
                  invoice.InvoiceDueAmount === 0 ? 'success' :
                  invoice.InvoicePaidAmount > 0 ? 'warning' : 'error'
                }
                size="small"
              />
            </Typography>
            <Typography>
              <strong>{t("invoice_pdf")}:</strong>{' '}
              {invoice.InvoiceUrl ? (
                <a href={invoice.InvoiceUrl} target="_blank" rel="noopener noreferrer">{t("download")}</a>
              ) : 'N/A'}
            </Typography>
          </Box>
          <Box>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<EditIcon />}
              sx={{ mr: 1 }}
              onClick={handleEdit}
            >
              {t("edit")}
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
            >
              {t("delete")}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Customer Info & Payment Summary side by side */}
      <Grid container spacing={3} alignItems="stretch" sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h5" color="primary" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
                {t("customer_info")}
              </Typography>
              <Typography><strong>Customer ID:</strong> {invoice.CustomerId}</Typography>
              {/* Add more customer fields if available */}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h5" color="primary" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
                {t("payment_summary")}
              </Typography>
              <Typography><strong>Total Amount:</strong> €{invoice.InvoicePrice}</Typography>
              <Typography><strong>Paid Amount:</strong> €{invoice.InvoicePaidAmount}</Typography>
              <Typography><strong>Due Amount:</strong> €{invoice.InvoiceDueAmount}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Divider sx={{ my: 4 }} />

      {/* Line Items */}
      <Typography variant="h6" color="primary" fontWeight={600} gutterBottom>
        {t("products_to_be_invoiced")}
      </Typography>
      <TableContainer component={Paper} sx={{ borderRadius: 2, mb: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ background: '#e3e6f0' }}>
              <TableCell><strong>{t("invoice_name")} / ID</strong></TableCell>
              <TableCell><strong>{t("quantity")}</strong></TableCell>
              <TableCell><strong>{t("price")}</strong></TableCell>
              <TableCell><strong>{t("vat")}</strong></TableCell>
              <TableCell><strong>{t("total_excl")}</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invoice.LineItems && invoice.LineItems.length > 0 ? (
              invoice.LineItems.map((p, idx) => (
                <TableRow key={idx}>
                  <TableCell>{p.ProductName || p.Name || p.ProductId}</TableCell>
                  <TableCell>{p.Quantity}</TableCell>
                  <TableCell>€{p.Price}</TableCell>
                  <TableCell>{p.VAT}%</TableCell>
                  <TableCell>€{p.TotalExcl}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ color: '#888' }}>
                  {t("no_line_items_found")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Totals */}
      <Box sx={{ textAlign: 'right', fontWeight: 600 }}>
        <div>{t("total_excl_vat")}: €{totalExcl.toFixed(2)}</div>
        <div>{t("total_vat")}: €{totalVAT.toFixed(2)}</div>
        <div>{t("total_incl_vat")}: €{totalIncl.toFixed(2)}</div>
      </Box>
    </Box>
  );
};

export default InvoiceViewPage;